// Recent performance — of the publishing, not of the audience.
//
// This is the card a reader arrives at expecting reach, impressions and
// follower growth, and it is the card that has to be clearest about why those
// are not here: we hold permission to publish to a Facebook Page, an Instagram
// professional account and a YouTube channel, and we have never requested the
// scopes that carry audience metrics. So the performance shown is the
// performance we can actually observe — what went out, when, and whether it
// arrived.
//
// Anything else would be a number invented by the dashboard, and a dashboard
// that invents one number cannot be trusted about the others.

import { Link } from "react-router-dom";
import { TrendChart } from "./charts";
import { dayLabel, nf } from "./format";
import type { Dashboard } from "./types";

export function PerformanceChartCard({ data }: { data: Dashboard }) {
  const points = data.daily.map((row) => ({
    label: dayLabel(row.day),
    title: dayLabel(row.day),
    value: row.published,
  }));
  const total = data.daily.reduce((sum, row) => sum + row.published, 0);
  const busiest = data.daily.reduce(
    (best, row) => (row.published > best.published ? row : best),
    data.daily[0] ?? { day: "", published: 0, failed: 0, scheduled: 0 },
  );
  const failedDays = data.daily.filter((row) => row.failed > 0).length;
  const perDay = total / Math.max(data.range.days, 1);

  return (
    <section className="card dash-card full performance-card" aria-labelledby="performance-head">
      <div className="card-head">
        <div>
          <h2 id="performance-head">Recent performance</h2>
          <p className="muted card-sub">
            Deliveries published per day over the last {data.range.days} days, counted in{" "}
            {data.range.timezone.replace("_", " ")}.
          </p>
        </div>
        <Link to="/posts" className="card-head-link">All posts →</Link>
      </div>

      {data.connections.active === 0 ? (
        <p className="muted empty-state">
          Connect a social account to see performance.{" "}
          <Link to="/connections">Connect one</Link> — nothing can go out until you do.
        </p>
      ) : total === 0 ? (
        <p className="muted empty-state">
          Nothing has gone out in this window yet. When it does, this is where the rhythm
          shows — the gaps matter as much as the peaks.
        </p>
      ) : (
        <>
          <div className="performance-figures">
            <div className="stat">
              <span className="stat-label muted">Deliveries published</span>
              <strong className="stat-value">{nf.format(total)}</strong>
              <span className="stat-hint muted">
                about {perDay.toFixed(perDay < 1 ? 1 : 0)} a day
              </span>
            </div>
            <div className="stat">
              <span className="stat-label muted">Busiest day</span>
              <strong className="stat-value">{nf.format(busiest.published)}</strong>
              <span className="stat-hint muted">
                {busiest.published > 0 ? dayLabel(busiest.day) : "—"}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label muted">Days with a failure</span>
              <strong className={failedDays > 0 ? "stat-value err" : "stat-value"}>
                {nf.format(failedDays)}
              </strong>
              <span className="stat-hint muted">
                {failedDays > 0
                  ? <Link to="/posts">see which posts</Link>
                  : "none in this window"}
              </span>
            </div>
          </div>

          <TrendChart points={points} label="Published deliveries per day" />
        </>
      )}
    </section>
  );
}
